// src/pages/CreateLesson.jsx
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/authContext';
import { supabase } from '../supabaseClient';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentTextIcon,
  QueueListIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';

const API_BASE = process.env.REACT_APP_API_URL;

export default function CreateLesson() {
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [level, setLevel] = useState('beginner');
  const [folder, setFolder] = useState('');
  const [reading, setReading] = useState('');
  const [writingPrompt, setWritingPrompt] = useState('');
  const [quiz, setQuiz] = useState([]);

  // Question composer
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    options: ['', '', ''],
    correct: '',
  });

  // Article generation
  const [lessonSource, setLessonSource] = useState('');
  const [generating, setGenerating] = useState(false);

  // Folders
  const [folders, setFolders] = useState([]);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      const { data, error } = await supabase
        .from('lesson_folders')
        .select('id, name')
        .eq('owner_id', user.id)
        .order('name');
      if (!error) setFolders(data);
    })();
  }, [user]);

  const allowWriting = useMemo(() => Boolean(writingPrompt.trim()), [writingPrompt]);

  const validateLesson = () => {
    if (!title.trim()) return 'Title is required';
    if (!reading.trim()) return 'Reading is required';
    for (let i = 0; i < quiz.length; i++) {
      const q = quiz[i];
      if (!q.text?.trim()) return `Question ${i + 1} is missing text`;
      if (!Array.isArray(q.options) || q.options.length < 2)
        return `Question ${i + 1} must have at least two options`;
      const uniq = new Set(q.options.map((o) => o.trim()));
      if (uniq.size !== q.options.length)
        return `Question ${i + 1} has duplicate options`;
      if (!q.correct || !uniq.has(q.correct.trim()))
        return `Question ${i + 1} correct answer must match one of the options`;
    }
    return '';
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !user?.id) return;
    const { data, error } = await supabase
      .from('lesson_folders')
      .insert([{ name: newFolderName.trim(), owner_id: user.id }])
      .select()
      .single();
    if (error) {
      setError(error.message || 'Failed to create folder.');
      return;
    }
    setFolders((prev) => [...prev, data]);
    setFolder(data.id);
    setNewFolderName('');
    setCreatingFolder(false);
  };

  const handleGenerateLesson = async () => {
    if (!lessonSource.trim()) {
      setError('Please paste an article URL.');
      return;
    }
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/generate-lesson-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: lessonSource.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not generate lesson');
      setReading(data.reading || '');
      setWritingPrompt(data.writing_prompt || '');
      setQuiz(Array.isArray(data.quiz) ? data.quiz : []);
      setSuccess('Lesson generated from article.');
    } catch (e) {
      setError(e.message || 'Could not generate lesson from URL.');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddOption = () => {
    if (newQuestion.options.length >= 5) return;
    setNewQuestion((q) => ({ ...q, options: [...q.options, ''] }));
  };

  const handleRemoveOption = (idx) => {
    const next = [...newQuestion.options];
    next.splice(idx, 1);
    setNewQuestion((q) => ({ ...q, options: next }));
  };

  const handleAddQuestion = () => {
    const trimmedOptions = newQuestion.options.map((o) => o.trim()).filter(Boolean);
    if (!newQuestion.text.trim()) {
      alert('Enter the question text.');
      return;
    }
    if (trimmedOptions.length < 2) {
      alert('Each question must have at least two options.');
      return;
    }
    const uniq = new Set(trimmedOptions);
    if (uniq.size !== trimmedOptions.length) {
      alert('Options must be unique.');
      return;
    }
    if (!newQuestion.correct.trim() || !uniq.has(newQuestion.correct.trim())) {
      alert('Correct answer must exactly match one of the options.');
      return;
    }
    setQuiz((prev) => [...prev, { ...newQuestion, options: trimmedOptions }]);
    setNewQuestion({ text: '', options: ['', '', ''], correct: '' });
  };

  const handleEditQuestion = (idx) => {
    setNewQuestion(quiz[idx]);
    setQuiz((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveLesson = async () => {
    setError('');
    setSuccess('');
    const msg = validateLesson();
    if (msg) {
      setError(msg);
      return;
    }
    if (!user?.id) {
      setError('Not authenticated.');
      return;
    }
    setSaving(true);
    try {
      const { error: insertError } = await supabase.from('lessons').insert([
        {
          title: title.trim(),
          level,
          folder_id: folder || null,
          reading: reading.trim(),
          writing_prompt: writingPrompt.trim() || null,
          allow_writing: allowWriting,
          quiz,
          owner_id: user.id,
        },
      ]);
      if (insertError) throw insertError;
      setSuccess('Lesson saved successfully.');
      // Reset the form but keep folder selection
      setTitle('');
      setReading('');
      setWritingPrompt('');
      setQuiz([]);
      setNewQuestion({ text: '', options: ['', '', ''], correct: '' });
    } catch (e) {
      setError(e.message || 'Failed to save lesson.');
    } finally {
      setSaving(false);
    }
  };

  if (!user)
    return <div className="p-6 text-gray-600">You must be logged in to create a lesson.</div>;
  if (user.role !== 'teacher')
    return <div className="p-6 text-gray-600">Access denied. Teachers only.</div>;

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-4 md:p-8">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Create a Lesson</h2>
          <p className="text-sm text-gray-600">
            Draft the reading, add a writing prompt, and build a quiz.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveLesson}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-500 disabled:opacity-60"
          >
            <CheckCircleIcon className="h-5 w-5" />
            {saving ? 'Saving...' : 'Save Lesson'}
          </button>
        </div>
      </header>

      {/* Alerts */}
      {success && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-800">
          {success}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 mt-0.5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Generate from URL */}
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <DocumentTextIcon className="h-5 w-5 text-yellow-600" /> Generate from Article URL
        </h3>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={lessonSource}
            onChange={(e) => setLessonSource(e.target.value)}
            placeholder="Paste article URL"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={handleGenerateLesson}
            disabled={generating}
            className="rounded-lg bg-yellow-500 px-4 py-2 text-white hover:bg-yellow-400 disabled:opacity-60"
          >
            {generating ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </section>

      {/* Core details */}
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Lesson Title</label>
          <input
            type="text"
            placeholder="Lesson title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Difficulty</label>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Folder</label>
          <div className="flex items-end gap-2">
            <select
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">No Folder</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => setCreatingFolder((v) => !v)}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-white hover:bg-green-500"
              title="Create new folder"
            >
              <PlusIcon className="h-5 w-5" /> New
            </button>
          </div>

          {creatingFolder && (
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="New folder name"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              <button
                onClick={handleCreateFolder}
                className="rounded-lg bg-blue-600 px-3 py-2 text-white hover:bg-blue-500"
              >
                Add
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Reading */}
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <label className="mb-1 block text-sm font-medium text-gray-700">Reading</label>
        <textarea
          rows={8}
          placeholder="Paste or write the reading..."
          value={reading}
          onChange={(e) => setReading(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
        />
        <p className="mt-1 text-xs text-gray-500">{reading.length} characters</p>
      </section>

      {/* Writing Prompt */}
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Writing Prompt (optional)
          </label>
          {allowWriting ? (
            <span className="text-xs text-green-700">Writing enabled for this lesson</span>
          ) : (
            <span className="text-xs text-gray-500">No writing prompt</span>
          )}
        </div>
        <textarea
          rows={4}
          placeholder="Add a writing prompt to enable writing practice"
          value={writingPrompt}
          onChange={(e) => setWritingPrompt(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
        />
      </section>

      {/* Quiz Builder */}
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
          <QueueListIcon className="h-5 w-5 text-purple-600" /> Quiz Questions
        </h3>

        {quiz.length === 0 ? (
          <p className="text-sm text-gray-600">No questions added yet.</p>
        ) : (
          <ul className="mb-6 grid gap-3">
            {quiz.map((q, idx) => (
              <li key={idx} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">
                      Q{idx + 1}. {q.text}
                    </p>
                    <ul className="ml-5 list-disc text-sm text-gray-700">
                      {q.options.map((opt, i) => (
                        <li
                          key={i}
                          className={opt === q.correct ? 'font-semibold text-green-700' : ''}
                        >
                          {opt}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="ml-3 flex shrink-0 items-center gap-2">
                    <button
                      className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      onClick={() => handleEditQuestion(idx)}
                    >
                      <PencilIcon className="mr-1 inline h-4 w-4" />
                      Edit
                    </button>
                    <button
                      className="rounded border px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                      onClick={() => setQuiz((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <TrashIcon className="mr-1 inline h-4 w-4" />
                      Remove
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="grid gap-3 rounded-lg border border-dashed border-gray-300 p-4">
          <input
            type="text"
            placeholder="Question text"
            value={newQuestion.text}
            onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />

          {newQuestion.options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                placeholder={`Option ${i + 1}`}
                value={opt}
                onChange={(e) => {
                  const options = [...newQuestion.options];
                  options[i] = e.target.value;
                  setNewQuestion({ ...newQuestion, options });
                }}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
              {i >= 2 && (
                <button
                  onClick={() => handleRemoveOption(i)}
                  className="rounded p-2 text-gray-500 hover:bg-gray-50"
                  title="Remove option"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          ))}

          {newQuestion.options.length < 5 && (
            <button
              onClick={handleAddOption}
              className="w-fit text-sm text-blue-600 hover:underline"
            >
              + Add Option
            </button>
          )}

          <input
            type="text"
            placeholder="Correct answer (must match an option exactly)"
            value={newQuestion.correct}
            onChange={(e) => setNewQuestion({ ...newQuestion, correct: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />

          <div className="flex justify-end">
            <button
              onClick={handleAddQuestion}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-500"
            >
              <PlusIcon className="h-5 w-5" />
              Add Question
            </button>
          </div>
        </div>
      </section>

      {/* Footer save (secondary) */}
      <div className="flex items-center justify-end">
        <button
          onClick={handleSaveLesson}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-500 disabled:opacity-60"
        >
          <CheckCircleIcon className="h-5 w-5" />
          {saving ? 'Saving...' : 'Save Lesson'}
        </button>
      </div>
    </div>
  );
}
